import { RouterModule } from '@angular/router';
import { LOCALE_ID, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LazyLoadImageModule } from 'ng-lazyload-image';

/** ANGULAR MATERIAL */
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatRippleModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

/** GENERAL MODULES */
import { CountdownModule } from 'ngx-countdown';
import { LayoutModule } from '@angular/cdk/layout';
// import { CurrencyMaskModule } from 'ng2-currency-mask';
import { GoogleMapsModule } from '@angular/google-maps';
import { IvyCarouselModule } from 'angular-responsive-carousel2';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { HttpClient, HttpClientModule, HttpClientJsonpModule } from '@angular/common/http';
import { CarouselModule } from 'ngx-owl-carousel-o';

/** COMPONENTS */
import { CartComponent } from './components/cart/cart.component';
import { LoaderComponent } from './components/loader/loader.component';
import { BannerComponent } from './components/banner/banner.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { SigninComponent } from './components/signin/signin.component';
import { SnackbarComponent } from './components/snackbar/snackbar.component';
import { DeliveryComponent } from './components/delivery/delivery.component';
import { BrochureComponent } from './components/brochure/brochure.component';
import { NewsletterComponent } from './components/newsletter/newsletter.component';
import { HomeFooterComponent } from './components/home-footer/home-footer.component';
import { AuthFooterComponent } from './components/auth-footer/auth-footer.component';
import { SuggestionsComponent } from './components/suggestions/suggestions.component';
import { AddressCardComponent } from './components/address-card/address-card.component';
import { ProductCartComponent } from './components/product-cart/product-cart.component';
import { ProductNoteComponent } from './components/product-note/product-note.component';
import { AuthToolbarComponent } from './components/auth-toolbar/auth-toolbar.component';
import { HomeToolbarComponent } from './components/home-toolbar/home-toolbar.component';
import { ProductCardComponent } from './components/product-card/product-card.component';
import { ProductPromoComponent } from './components/product-promo/product-promo.component';
import { ProductSummaryComponent } from './components/product-summary/product-summary.component';
import { AddressAutocompleteComponent } from './components/address-autocomplete/address-autocomplete.component';
import { ProductBannersComponent } from './components/product-banners/product-banners.component';
import { DeleteMissingProductsModalComponent } from './components/delete-missing-products-modal/delete-missing-products-modal.component';
import { AddProductListsComponent } from './components/add-product-lists/add-product-lists.component';
import { NotNearStoresModalComponent } from './components/not-near-stores-modal/not-near-stores-modal.component';
import { CreateListModalComponent } from './components/create-list-modal/create-list-modal.component';
import { PaypalSmartButtonComponent } from './components/paypal-smart-button/paypal-smart-button.component';
import { ProductListComponent } from './components/product-list/product-list.component';
import { RemoveProductConfirmationComponent } from './components/remove-product-confirmation/remove-product-confirmation.component';
import { DeletePaymentMethodsModalComponent } from './components/delete-payment-methods-modal/delete-payment-methods-modal.component';
import { WarnMissingProductsComponent } from './components/warn-missing-products/warn-missing-products.component';
import { InviteUserModalComponent } from './components/invite-user-modal/invite-user-modal.component';
import { SureContinueComponent } from './components/sure-continue/sure-continue.component';
import { CancelConfirmationModalComponent } from './components/cancel-confirmation-modal/cancel-confirmation-modal.component';
import { PaymentsApproveComponent } from './components/payments-approve/payments-approve.component';
import { VideoModalComponent } from './components/video-modal/video-modal.component';

import { ProductPurchaseComponent } from './components/product-purchase/product-purchase.component';
import { WelcomeAlsuperModalComponent } from './components/welcome-alsuper-modal/welcome-alsuper-modal.component';
import { CedisModalComponent } from './components/cedis-modal/cedis-modal.component';
import { LandingDownloadAppsComponent } from './components/landing-download-apps/landing-download-apps.component';
import { ExtraChargeModalComponent } from './components/extra-charge-modal/extra-charge-modal.component';
import { PrivacyModalComponent } from './components/privacy-modal/privacy-modal.component';
import { UserDataIncompletedComponent } from './components/user-data-incompleted/user-data-incompleted.component';
import { PdpImagesModalComponent } from './components/pdp-images-modal/pdp-images-modal.component';
import { ChangeHourModalComponent } from './components/change-hour-modal/change-hour-modal.component';
import { AlertsAllPointsComponent } from './components/alerts-all-points/alerts-all-points.component';
import { CpInviteModeModalComponent } from './components/cp-invite-mode-modal/cp-invite-mode-modal.component';
import { ModalComponent } from './components/modal/modal.component';
import { ErrorComponentComponent } from './components/error-component/error-component.component';
import { DetailListCardComponent } from './components/list-card/detail-list-card/detail-list-card.component';
import { ListCardComponent } from './components/list-card/list-card.component';
import { ConfirmationsModalComponent } from './components/confirmations-modal/confirmations-modal.component';
import { ConfirmActionComponent } from './components/confirm-action/confirm-action.component';
import { MobileMenuModalComponent } from './components/mobile-menu-modal/mobile-menu-modal.component';
import { GeneralCarouselComponent } from './components/general-carousel/general-carousel.component';
import { SmartLabelComponent } from './components/smart-label/smart-label.component';
import { SupermixListsComponent } from '../supermix-lists/supermix-lists.component';
import { PromoExpiredComponent } from './components/promo-expired/promo-expired.component';
import { PhoneVerifyComponent } from './components/phone-verify/phone-verify.component';
import { PhoneVerifyModalComponent } from './components/phone-verify-modal/phone-verify-modal.component';

export function HttpLoaderFactory(http: HttpClient): any {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  imports: [
    FormsModule,
    RouterModule,
    CommonModule,
    LayoutModule,
    MatIconModule,
    MatCardModule,
    MatListModule,
    MatMenuModule,
    MatTabsModule,
    MatChipsModule,
    MatInputModule,
    MatBadgeModule,
    MatRadioModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatSliderModule,
    CountdownModule,
    MatRippleModule,
    MatSidenavModule,
    HttpClientModule,
    MatDividerModule,
    MatToolbarModule,
    GoogleMapsModule,
    MatCheckboxModule,
    MatGridListModule,
    MatSnackBarModule,
    IvyCarouselModule,
    MatExpansionModule,
    MatFormFieldModule,
    // CurrencyMaskModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    HttpClientJsonpModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CarouselModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      isolate: false
    }),
    LazyLoadImageModule,
    CartComponent,
    BannerComponent,
    DialogComponent,
    LoaderComponent,
    SigninComponent,
    SnackbarComponent,
    DeliveryComponent,
    BrochureComponent,
    HomeFooterComponent,
    AuthFooterComponent,
    NewsletterComponent,
    AuthToolbarComponent,
    HomeToolbarComponent,
    ProductCardComponent,
    AddressCardComponent,
    ProductCartComponent,
    ProductNoteComponent,
    SuggestionsComponent,
    ProductPromoComponent,
    ProductSummaryComponent,
    PromoExpiredComponent,
    AddressAutocompleteComponent,
    ProductBannersComponent,
    DeleteMissingProductsModalComponent,
    AddProductListsComponent,
    NotNearStoresModalComponent,
    CreateListModalComponent,
    PaypalSmartButtonComponent,
    ProductListComponent,
    RemoveProductConfirmationComponent,
    DeletePaymentMethodsModalComponent,
    WarnMissingProductsComponent,
    InviteUserModalComponent,
    SureContinueComponent,
    CancelConfirmationModalComponent,
    PaymentsApproveComponent,
    VideoModalComponent,
    ProductPurchaseComponent,
    WelcomeAlsuperModalComponent,
    CedisModalComponent,
    LandingDownloadAppsComponent,
    ExtraChargeModalComponent,
    PrivacyModalComponent,
    UserDataIncompletedComponent,
    PdpImagesModalComponent,
    SupermixListsComponent,
    ChangeHourModalComponent,
    AlertsAllPointsComponent,
    CpInviteModeModalComponent,
    ModalComponent,
    ErrorComponentComponent,
    ListCardComponent,
    DetailListCardComponent,
    ConfirmationsModalComponent,
    ConfirmActionComponent,
    MobileMenuModalComponent,
    GeneralCarouselComponent,
    SmartLabelComponent,
    PhoneVerifyComponent,
    PhoneVerifyModalComponent
  ],
  exports: [
    FormsModule,
    RouterModule,
    LayoutModule,
    MatIconModule,
    MatCardModule,
    MatListModule,
    MatMenuModule,
    MatTabsModule,
    MatChipsModule,
    MatInputModule,
    MatBadgeModule,
    MatRadioModule,
    MatChipsModule,
    MatSliderModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    CountdownModule,
    TranslateModule,
    BannerComponent,
    DialogComponent,
    LoaderComponent,
    MatRippleModule,
    SigninComponent,
    MatSidenavModule,
    HttpClientModule,
    MatDividerModule,
    MatToolbarModule,
    GoogleMapsModule,
    MatCheckboxModule,
    MatGridListModule,
    MatSnackBarModule,
    IvyCarouselModule,
    SnackbarComponent,
    DeliveryComponent,
    BrochureComponent,
    MatExpansionModule,
    MatFormFieldModule,
    // CurrencyMaskModule,
    MatNativeDateModule,
    AuthFooterComponent,
    ReactiveFormsModule,
    MatDatepickerModule,
    HomeFooterComponent,
    NewsletterComponent,
    AuthToolbarComponent,
    MatSlideToggleModule,
    HomeToolbarComponent,
    ProductCardComponent,
    AddressCardComponent,
    ProductCartComponent,
    ProductNoteComponent,
    SuggestionsComponent,
    MatProgressBarModule,
    HttpClientJsonpModule,
    MatTooltipModule,
    ProductPromoComponent,
    ProductSummaryComponent,
    PromoExpiredComponent,
    MatProgressSpinnerModule,
    AddressAutocompleteComponent,
    ProductBannersComponent,
    NotNearStoresModalComponent,
    CreateListModalComponent,
    PaypalSmartButtonComponent,
    ProductListComponent,
    RemoveProductConfirmationComponent,
    ProductPurchaseComponent,
    CarouselModule,
    CedisModalComponent,
    LandingDownloadAppsComponent,
    LazyLoadImageModule,
    ListCardComponent,
    DetailListCardComponent,
    GeneralCarouselComponent,
    SmartLabelComponent,
    PhoneVerifyComponent,
    PhoneVerifyModalComponent
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'es-MX'
    }
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export class SharedModule { }
